import queryString from 'query-string'

import { URL_API } from 'utils/envHelper'

export type QueryParams = Record<string, any>

export type HttpError = Error & { status?: number; body?: unknown }

// Standard paginated list response from the management API.
export type Paginated<T> = { count: number; items: T[] }

type RequestOptions = {
  method?: string
  body?: unknown
  query?: QueryParams
}

// Generic defaults to `any` (not `unknown`) so untyped callers keep compiling during the hook-by-hook typing migration; flip to `unknown` once all callers pass a response type.
const fetchBuilder = async <T = any>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { method, body, query } = options

  const _path = [path, queryString.stringify({ ...query })].filter(Boolean).join('?')

  const res = await fetch(`${URL_API}${_path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (res.ok) {
    try {
      return await res.json()
    } catch {
      return res as T
    }
  }

  // Throw an Error (not the raw Response) so callers/Bugsnag get a real message; `status` is preserved (several callers branch on it) and the parsed body attached as `body`.
  let errorBody: any
  try {
    errorBody = await res.json()
  } catch {
    errorBody = undefined
  }
  const error: HttpError = Object.assign(new Error(`Request failed with status ${res.status}`), {
    status: res.status,
    body: errorBody,
  })
  throw error
}

export const get = <T = any>(path: string, options?: { query?: QueryParams }): Promise<T> =>
  fetchBuilder<T>(path, { method: 'GET', ...options })
export const post = <T = any>(path: string, body?: unknown, options?: { query?: QueryParams }): Promise<T> =>
  fetchBuilder<T>(path, { method: 'POST', ...options, body })
export const patch = <T = any>(path: string, body?: unknown, options?: { query?: QueryParams }): Promise<T> =>
  fetchBuilder<T>(path, { method: 'PATCH', ...options, body })
export const put = <T = any>(path: string, body?: unknown, options?: { query?: QueryParams }): Promise<T> =>
  fetchBuilder<T>(path, { method: 'PUT', ...options, body })
export const del = <T = any>(path: string, body?: unknown, options?: { query?: QueryParams }): Promise<T> =>
  fetchBuilder<T>(path, { method: 'DELETE', ...options, body })

// No status = network/transport failure, so treat it as a server-side problem too.
export const isServerError = (status?: number) => !status || status >= 500
